/**
 *
 * CostCalculatorForm
 *
 */

// @flow

import { fileDownloader } from '@fil-global/eiswagen';
import { Button, Cell, Grid, GridContainer, Spinner } from '@fil-global/eiswurfel';
import classnames from 'classnames';
import _get from 'lodash/get';
import React, { useLayoutEffect, useRef, useState } from 'react';

import { useStaticLabelsContext } from '../../global/context';
import { formatter, httpClient } from '../../utils';
import FundSuggestor from '../FundSuggestor';
import QuestionGrid from '../QuestionGrid';
import SummaryView from '../SummaryView';
import {
  AFTER_NEXT_QUESTION,
  EDIT_QUESTION,
  NEXT_QUESTION,
  PRE_EDIT_QUESTION,
  UPDATE_FIELD_VALUE,
} from './CostCalculatorForm.constants';
import { useFormDispatchContext, useFormStateContext } from './CostCalculatorForm.context';
import {
  selectAnsweredQuestions,
  selectCurrentQuestionIndex,
  selectIsAfterNext,
  selectIsPreEdit,
  selectPreEditQuestionIndex,
  selectQuestionsToBeAnswered,
  selectVisibleQuestions,
} from './CostCalculatorForm.selectors';

export const getCustomController = configs => {
  if (configs.inputComponent !== 'custom') {
    return null;
  }

  if (configs.customComponent === 'fundsuggestor') {
    return <FundSuggestor {...configs} />;
  }
  return null;
};

const CostCalculatorForm = () => {
  const state = useFormStateContext();
  const dispatch = useFormDispatchContext();
  const staticLabels = useStaticLabelsContext();
  const [isDownloadingPdf, setIsDownloadingPdf] = useState(false);
  const firstUpdateRef = useRef(true);
  const scrollToElementRef = useRef(null);

  const currentQuestionIndex = selectCurrentQuestionIndex(state);
  const preEditQuestionIndex = selectPreEditQuestionIndex(state);
  const questionsToBeAnswered = selectQuestionsToBeAnswered(state);
  const answeredQuestions = selectAnsweredQuestions(state);
  const visibleQuestions = selectVisibleQuestions(state);
  const isAfterNext = selectIsAfterNext(state);
  const isPreEdit = selectIsPreEdit(state);

  const visibleQuestionCount = visibleQuestions.length;
  const isFirstQuestion = visibleQuestionCount === 1;

  // Scroll to the current question when questions are updated.
  useLayoutEffect(() => {
    // Avoid scrolling on first render.
    if (firstUpdateRef.current) {
      firstUpdateRef.current = false;
      return;
    }

    /* istanbul ignore else */
    if (scrollToElementRef.current) {
      scrollToElementRef.current.scrollIntoView(false);
    }
  }, [visibleQuestions]);

  const onValueChange = (fieldName, fieldValue) => {
    dispatch({ type: UPDATE_FIELD_VALUE, fieldName, fieldValue });
  };

  const goToNextQuestion = () => {
    const nextQuestion = questionsToBeAnswered.find(q => q.index > currentQuestionIndex);

    dispatch({ type: NEXT_QUESTION, nextIndex: _get(nextQuestion, 'index', null) });

    setTimeout(() => {
      dispatch({ type: AFTER_NEXT_QUESTION });
    }, 500);
  };

  const goToEditMode = index => {
    dispatch({ type: PRE_EDIT_QUESTION, index });

    setTimeout(() => {
      dispatch({ type: EDIT_QUESTION, index });
    }, 500);
  };

  const downloadPdf = async () => {
    // Get any specified transforms to convert answers before request.
    const transforms = _get(staticLabels, 'summary.transforms', {});

    // Get all transformed answers.
    const answers = answeredQuestions.reduce(
      (acc, { fieldName, fieldValue }) => ({
        ...acc,
        [fieldName]: _get(fieldValue, transforms[fieldName], fieldValue),
      }),
      {}
    );

    // Build api and request for download.
    const downloadPdfApiDetails = _get(staticLabels, 'apis.downloadPdf', {});
    const host = _get(downloadPdfApiDetails, ['domains', window.location.hostname], '');
    const route = _get(downloadPdfApiDetails, 'route', '');
    const api = `${host}${route}`;

    // Do request to download PDF.
    setIsDownloadingPdf(true);

    try {
      const { data } = await httpClient.post(api, answers);

      fileDownloader(data.data, 'application/pdf', 'Vorab_Kosteninformation');
    } catch (error) {
      // TODO: Handle error.
    } finally {
      setIsDownloadingPdf(false);
    }
  };

  const renderSummaryContent = () => {
    const answers = answeredQuestions.reduce(
      (acc, { fieldName, fieldValue }) => ({ ...acc, [fieldName]: fieldValue }),
      {}
    );
    const description = _get(staticLabels, 'summary.description', '');
    const notes = _get(staticLabels, 'summary.notes', []);
    const fieldAnswers = _get(staticLabels, 'summary.fieldAnswers', {});
    const matches = description.match(/\{(.*?)\}/g);
    let formattedDescription = description;

    matches.forEach(m => {
      const name = m.replace('{', '').replace('}', '');
      const format = _get(fieldAnswers, [name, 'format']);
      const use = _get(fieldAnswers, [name, 'use'], '');
      const answer = _get(answers, use, '');
      const value = _get(fieldAnswers, [name, 'values', answer], answer);
      const finalValue = formatter({ format, value });

      formattedDescription = formattedDescription.replace(new RegExp(m, 'g'), finalValue);
    });

    formattedDescription = formattedDescription.replace(/\s{2,}/g, ' ');

    return <SummaryView description={formattedDescription} notes={notes} />;
  };

  return (
    <GridContainer extraClasses="cost-calculator-form" variation=" ">
      {isDownloadingPdf && (
        <Spinner
          displayText={staticLabels.loaders.downloadPdf}
          displayType="fixed"
          isVisible
          size="medium"
        />
      )}

      {visibleQuestions.map((vq, i) => {
        const wrapperGoToEditMode = () => goToEditMode(vq.index);
        const isEditMode = vq.index === currentQuestionIndex;
        const configs = {
          ...vq,
          goToEditMode: wrapperGoToEditMode,
          goToNextQuestion,
          isEditMode,
          onValueChange,
          extraClasses: classnames({
            answered: !isEditMode,
            'slide-down-out': isPreEdit && !isFirstQuestion && vq.index > preEditQuestionIndex,
            'slide-up-in': !isAfterNext && !isFirstQuestion && visibleQuestionCount === i + 1,
          }),
        };

        return (
          <QuestionGrid {...configs} key={i.toString()} questionNumber={i + 1}>
            {getCustomController(configs)}
          </QuestionGrid>
        );
      })}

      {questionsToBeAnswered.length === answeredQuestions.length && (
        <GridContainer extraClasses={classnames({ 'slide-down-out': isPreEdit })}>
          {renderSummaryContent()}

          <Grid>
            <Cell>
              <Button disabled={isDownloadingPdf} onClick={downloadPdf}>
                {staticLabels.buttons.submit}
              </Button>
            </Cell>
          </Grid>
        </GridContainer>
      )}

      <div id="scrollToElement" ref={scrollToElementRef} />
    </GridContainer>
  );
};

CostCalculatorForm.defaultProps = {};

export default CostCalculatorForm;
